import { Button, EmptyState, Table, useDrawer } from '@iveco-ti/iveco-ds'
import { Plus } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { ExportDropdown } from '@/common/components/ExportDropdown'
import { FilterButton } from '@/common/components/FilterButton'
import { OrderByDropdown } from '@/common/components/OrderByDropdown'
import { useDownloadExport } from '@/common/hooks/useDownloadExport'
import { DrawerEditGenericContent } from './components/DrawerEditGenericContent'
import { DrawerFilterGenericContent } from './components/DrawerFilterGenericContent'
import { DrawerNewGenericContent } from './components/DrawerNewGenericContent'
import { DrawerViewGenericContent } from './components/DrawerViewGenericContent'
import { GenericContentTableRow } from './components/GenericContentTableRow'
import { TableRowSkeleton } from './components/TableRowSkeleton'
import { useGenericContent } from './hooks/useGenericContent'

export default function GenericContent() {
  const { t } = useTranslation(['settings', 'common'])
  const {
    genericContentBadges,
    handleGenericContentSearch,
    genericContentOrderBy,
    handleChangeOrder,
    filtersQty,
    isLoadingGenericContent,
    genericContent,
    genericContentPaginated,
    handleChangePage,
    contentsPerPage,
  } = useGenericContent()
  const { downloadExport, isDownloadingExport } = useDownloadExport({
    route: '/generic-content/report',
  })
  const drawerFilterGenericContent = useDrawer('drawer-filter-generic-content')
  const drawerNewGenericContent = useDrawer('drawer-new-generic-content')

  const ORDER_BY_OPTIONS = [
    {
      label: t('generic_content.table.order_dropdown.fields.tag_content'),
      field: 'tagContent',
    },
    {
      label: t('generic_content.table.order_dropdown.fields.field_key'),
      field: 'fieldKey',
    },
  ]

  const TABLE_COLUMNS = [
    {
      label: t('generic_content.table.table_head.column_first'),
      badge: genericContentBadges?.tagContent,
      order: 'tagContent',
    },
    {
      label: t('generic_content.table.table_head.column_second'),
      order: 'fieldKey',
    },
    {
      label: t('generic_content.table.table_head.column_third'),
      order: 'fieldValue',
    },
    {
      label: t('generic_content.table.table_head.column_fourth'),
      end: true,
    },
  ]

  return (
    <>
      <div className="overflow-y-auto h-full shadow-sm rounded-xl bg-white">
        <HeadProvider>
          <Title>{t('generic_content.browser.title')}</Title>
        </HeadProvider>

        <Table.Root>
          <Table.Search
            searchName="search"
            searchPlaceholder={t(
              'common:authenticated_layout.search_input.placeholder',
            )}
            onSearch={handleGenericContentSearch}
          >
            <OrderByDropdown
              selected={genericContentOrderBy}
              onChangeOrder={handleChangeOrder}
              options={ORDER_BY_OPTIONS}
            />

            <ExportDropdown
              onExportCSV={() => downloadExport({}, 'csv')}
              onExportXLSX={() => downloadExport({}, 'xlsx')}
              isExporting={isDownloadingExport}
            />

            <FilterButton
              open={drawerFilterGenericContent.open}
              filters={filtersQty}
            />

            <Button.Root onClick={drawerNewGenericContent.open}>
              <Button.Icon icon={Plus} />
            </Button.Root>
          </Table.Search>

          {isLoadingGenericContent && <TableRowSkeleton />}

          {!genericContent.length && !isLoadingGenericContent && (
            <EmptyState
              title={t('generic_content.empty_state.title')}
              description={t('generic_content.empty_state.description')}
            />
          )}

          <Table.Content>
            {!isLoadingGenericContent && !!genericContent.length && (
              <>
                <Table.Head
                  hideCheckbox
                  cols={TABLE_COLUMNS}
                  onOrderBy={(order) => handleChangeOrder(order, 'asc')}
                />

                <Table.Body>
                  {genericContent.map((content) => (
                    <GenericContentTableRow
                      key={content.id}
                      genericContent={content}
                    />
                  ))}
                </Table.Body>
              </>
            )}
          </Table.Content>

          <Table.Footer
            page={genericContentPaginated?.currentPage || 1}
            totalItems={genericContentPaginated?.totalItems || 0}
            totalItemsLabel={t(
              'generic_content.table.table_footer.total_registers.label',
            )}
            totalPages={genericContentPaginated?.totalPages || 1}
            ofText={t('common:components.table_footer.pagination.label')}
            onChangePage={handleChangePage}
            itemsPerPage={contentsPerPage}
          />
        </Table.Root>
      </div>

      <DrawerFilterGenericContent />
      <DrawerNewGenericContent />
      <DrawerEditGenericContent />
      <DrawerViewGenericContent />
    </>
  )
}
